// The previews of every development sandbox, an item of Me's settings
// (/me/settings/previews) for the people allowed one and the operators; others
// do not see the item, and its path sends them to the settings. Grouped by
// sandbox (its owner and state); a sandbox that is not running has no list (it
// is emptied when it sleeps), only its heading. The owner and operators can
// stop a preview (the server says `canStop`).
//
// Promise:
// - The list is read again on `preview.list` (part.tsx); never polled.
// - A preview that is not up (could not start, no worktree, a name made
//   twice) is not a link: there is nothing to open.
// - Stopping takes the preview off the list on the server's next answer; until
//   then its row says it is stopping. A refusal is said under that row,
//   naming it.

import { useQuery } from "@tanstack/react-query";
import { Navigate } from "@tanstack/react-router";
import type {
  PreviewRowDTO,
  PreviewSandboxDTO,
} from "@trameria/cloud-api/sandboxes-shared";
import { m } from "@trameria/cloud-i18n";
import {
  apiErrorWithLead,
  Button,
  ErrorLine,
  iconOf,
  type SettingsItem,
  Skeleton,
  useApi,
  useFormat,
} from "@trameria/ui-core";
import { accountQuery } from "../queries";
import { meCloudQuery, previewListQuery, useStopPreview } from "./queries";
import { phaseLabel, phaseOf } from "./words";

const STATUS: Record<PreviewRowDTO["status"], () => string> = {
  running: () => m.sandboxes_previews_status_running(),
  failed: () => m.sandboxes_previews_status_failed(),
  missing: () => m.sandboxes_previews_status_missing(),
  conflict: () => m.sandboxes_previews_status_conflict(),
};

// The name takes the rest; the state and the button keep their width.
const COLUMNS = "minmax(0,1fr) 140px 64px";

/** Whether the person may see the previews (undefined until it is known). */
export function useMaySeePreviews(): boolean | undefined {
  const api = useApi();
  const account = useQuery(accountQuery(api));
  const mine = useQuery(meCloudQuery(api));
  if (account.data?.role === "admin") return true;
  if (mine.data?.dev.allowed) return true;
  if (account.isPending || mine.isPending) return undefined;
  return false;
}

export const previewsSettings: SettingsItem = {
  id: "previews",
  order: 45,
  label: () => m.sandboxes_previews_title(),
  icon: iconOf("eye"),
  description: () => m.sandboxes_previews_description(),
  useShown: () => useMaySeePreviews() === true,
  Component: Previews,
};

function Previews() {
  const may = useMaySeePreviews();
  if (may === false) return <Navigate to="/me/settings" replace />;
  if (may === undefined)
    return (
      <div className="mt-6">
        <Skeleton lines={[60, 45, 52]} />
      </div>
    );
  return <PreviewList />;
}

function PreviewList() {
  const api = useApi();
  const list = useQuery(previewListQuery(api));
  return (
    <>
      {list.isError && !list.data ? (
        <div className="mt-6 flex items-center gap-3 text-[13px] text-muted">
          {m.web_load_failed()}
          <Button onClick={() => void list.refetch()}>{m.web_retry()}</Button>
        </div>
      ) : !list.data ? (
        <div className="mt-6">
          <Skeleton lines={[60, 45, 52]} />
        </div>
      ) : list.data.sandboxes.length === 0 ? (
        <p className="mt-6 text-[13px] text-muted">
          {m.sandboxes_previews_empty()}
        </p>
      ) : (
        <div className="mt-5 flex flex-col gap-4">
          {list.data.sandboxes.map((s) => (
            <SandboxGroup key={s.sandbox} sandbox={s} />
          ))}
        </div>
      )}
    </>
  );
}

function SandboxGroup({ sandbox: s }: { sandbox: PreviewSandboxDTO }) {
  const format = useFormat();
  const seen = s.previewSeenAt
    ? m.sandboxes_previews_last_seen({ time: format.dateTime(s.previewSeenAt) })
    : null;
  // The list has no device to read: a listed sandbox has run before.
  const state = phaseLabel(
    phaseOf({ status: s.status, want: "awake" }, true),
    null,
  );
  return (
    <section data-row={`preview-sandbox:${s.sandbox}`}>
      <div className="flex items-baseline gap-2 px-[10px] pb-1 text-[12.5px] text-body">
        <span>{s.owner.name ?? s.owner.userId}</span>
        <span className="text-[11.5px] text-faint tabular-nums">
          {[state, seen].filter(Boolean).join(" · ")}
        </span>
      </div>
      {/* Not running: its heading says so; no second line repeating it. */}
      {s.status !== "running" ? null : s.previews.length === 0 ? (
        <p className="px-[10px] text-[12px] text-faint">
          {m.sandboxes_previews_none_running()}
        </p>
      ) : (
        s.previews.map((p) => (
          <PreviewRow
            key={p.name}
            sandbox={s.sandbox}
            canStop={s.canStop}
            preview={p}
          />
        ))
      )}
    </section>
  );
}

function PreviewRow({
  sandbox,
  canStop,
  preview: p,
}: {
  sandbox: string;
  canStop: boolean;
  preview: PreviewRowDTO;
}) {
  // One per row: its failure and its "stopping" stay with it.
  const stop = useStopPreview();
  const offline = stop.data?.status === "offline";
  const stopping =
    stop.isPending || (stop.isSuccess && stop.data.status === "requested");
  const failure = stop.isError
    ? apiErrorWithLead(
        stop.error,
        m.sandboxes_previews_stop_failed({ name: p.name }),
      )
    : offline
      ? m.sandboxes_previews_stop_offline({ name: p.name })
      : null;
  return (
    <div>
      <div
        className="grid items-center gap-x-[10px] rounded-[7px] px-[10px] py-[6px] hover:bg-hover"
        style={{ gridTemplateColumns: COLUMNS }}
      >
        {p.status === "running" ? (
          <a
            href={p.url}
            target="_blank"
            rel="noreferrer"
            className="min-w-0 truncate text-[12.5px] text-accent underline-offset-2 hover:underline"
          >
            {p.name}
          </a>
        ) : (
          <span className="min-w-0 truncate text-[12.5px] text-body">
            {p.name}
          </span>
        )}
        <span className="text-[12px] text-faint">
          {stopping ? m.sandboxes_previews_stopping() : STATUS[p.status]()}
        </span>
        <span className="flex justify-end">
          {canStop && p.status === "running" && (
            <button
              type="button"
              aria-label={m.sandboxes_previews_stop_label({ name: p.name })}
              disabled={stopping}
              onClick={() => stop.mutate({ sandbox, name: p.name })}
              className="box-border inline-flex h-[26px] cursor-pointer items-center rounded-[13px] px-2 pb-px text-[12px] text-muted hover:bg-hover hover:text-body disabled:cursor-default disabled:opacity-50"
            >
              {m.sandboxes_previews_stop()}
            </button>
          )}
        </span>
      </div>
      {failure && (
        <div className="px-[10px] pb-1">
          <ErrorLine>{failure}</ErrorLine>
        </div>
      )}
    </div>
  );
}
